import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import { TopNavBar } from '@/components/TopNavBar';
import PropertiesPage from '@/pages/PropertiesPage';
import TenantsPage from '@/pages/TenantsPage';
import LeadsPage from '@/pages/LeadsPage';
import IncomePage from '@/pages/IncomePage';
import VacancyPage from '@/pages/VacancyPage';
import CalendarPage from '@/pages/CalendarPage';
import FeaturesPage from '@/pages/FeaturesPage';
import NotesPage from '@/pages/NotesPage';
import ParkingPage from '@/pages/ParkingPage';
import DoorCodesPage from '@/pages/DoorCodesPage';
import MarketingPage from '@/pages/MarketingPage';
import LoginInfoPage from '@/pages/LoginInfoPage';
import MoveInOutPage from '@/pages/MoveInOutPage';
import HousekeepingPage from '@/pages/HousekeepingPage';
import MaintenancePage from '@/pages/MaintenancePage';
import TransactionsPage from '@/pages/TransactionsPage';
import DepositsPage from '@/pages/DepositsPage';
import RentTrackingPage from '@/pages/RentTrackingPage';
import ListingsPage from '@/pages/ListingsPage';
import ListingDetailPage from '@/pages/ListingDetailPage';
import FlexLivingLanding from '@/pages/FlexLivingLanding';
import { CollectionIndexPage, CollectionListingPage } from '@/pages/CollectionPages';
import DashboardPage from '@/pages/DashboardPage';
import FinancialHealthPage from '@/pages/FinancialHealthPage';
import ReportsPage from '@/pages/ReportsPage';
import FlexibleReportsPage from '@/pages/FlexibleReportsPage';
import FurnitureProjectsPage from '@/pages/FurnitureProjectsPage';
import FurnitureProjectDetailPage from '@/pages/FurnitureProjectDetailPage';
import FurnitureStaffingShell from '@/pages/FurnitureStaffingShell';
import AdminStaffHoursPage from '@/pages/StaffHoursPage';
import AdminStaffTasksPage from '@/pages/AdminStaffTasksPage';
import { StaffLoginPage, StaffRegisterPage, StaffResetPasswordPage } from '@/pages/StaffAuthPages';
import { StaffHomePage, StaffProjectsPage, StaffHoursPage, StaffProfilePage } from '@/pages/staff/StaffPages';
import { StaffPayPeriodsPage } from '@/pages/staff/StaffPayPeriodsPage';
import { StaffTasksPage } from '@/pages/staff/StaffTasksPage';
import { RequireStaffAuth } from '@/components/staff/RequireStaffAuth';
import { StaffAuthProvider } from '@/contexts/StaffAuthContext';
import { AppAuthProvider } from '@/contexts/AppAuthContext';
import { RequireAppAuth } from '@/components/RequireAppAuth';
import AppLoginPage from '@/pages/AppLoginPage';
import AppRegisterPage from '@/pages/AppRegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import AccountSettingsPage from '@/pages/AccountSettingsPage';
import { RedirectIfAuthed } from '@/components/RedirectIfAuthed';
import AdminUsersPage from '@/pages/AdminUsersPage';
import ActivityLogPage from '@/pages/ActivityLogPage';
import FurnitureRoomDefaultsPage from '@/pages/FurnitureRoomDefaultsPage';
import ReceiptProcessingPage from '@/pages/ReceiptProcessingPage';
import { FurnitureNavBar } from '@/components/furniture/FurnitureNavBar';
import RoomMoodBoardPage from '@/pages/RoomMoodBoardPage';
import ProjectViewPage from '@/pages/ProjectViewPage';
import PublicBreakdownPage from '@/pages/PublicBreakdownPage';
import { PasswordGate } from '@/components/PasswordGate';
import KeyboardShortcutsHelp from '@/components/KeyboardShortcutsHelp';
function AppShell({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <TopNavBar />
      <main className="pt-[88px]">
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto">
          {children}
        </div>
      </main>
      <KeyboardShortcutsHelp />
    </div>
  );
}

function ProtectedApp() {
  return (
    <PasswordGate>
      <RequireAppAuth>
        <AppShell>
          <ErrorBoundary>
            <Routes>
              {/* Dashboard section */}
              <Route path="/" element={<RequireAppAuth page="dashboard"><DashboardPage /></RequireAppAuth>} />
              <Route path="/notes" element={<RequireAppAuth page="notes"><NotesPage /></RequireAppAuth>} />
              <Route path="/move-in-out" element={<RequireAppAuth page="move-in-out"><MoveInOutPage /></RequireAppAuth>} />
              <Route path="/housekeeping" element={<RequireAppAuth page="housekeeping"><HousekeepingPage /></RequireAppAuth>} />

              {/* Calendar section */}
              <Route path="/tenants" element={<RequireAppAuth page="tenants"><TenantsPage /></RequireAppAuth>} />
              <Route path="/leads" element={<RequireAppAuth page="leads"><LeadsPage /></RequireAppAuth>} />
              <Route path="/vacancy" element={<RequireAppAuth page="vacancy"><VacancyPage /></RequireAppAuth>} />
              <Route path="/properties" element={<RequireAppAuth page="properties"><PropertiesPage /></RequireAppAuth>} />
              <Route path="/units" element={<Navigate to="/properties" replace />} />
              <Route path="/rent-roll" element={<RequireAppAuth page="finance"><IncomePage /></RequireAppAuth>} />
              <Route path="/calendar" element={<RequireAppAuth page="calendar"><CalendarPage /></RequireAppAuth>} />

              {/* Operations section */}
              <Route path="/rent-tracking" element={<RequireAppAuth page="operations"><RentTrackingPage /></RequireAppAuth>} />
              <Route path="/maintenance" element={<RequireAppAuth page="operations"><MaintenancePage /></RequireAppAuth>} />
              <Route path="/parking" element={<RequireAppAuth page="operations"><ParkingPage /></RequireAppAuth>} />
              <Route path="/door-codes" element={<RequireAppAuth page="operations"><DoorCodesPage /></RequireAppAuth>} />
              <Route path="/marketing" element={<RequireAppAuth page="operations"><MarketingPage /></RequireAppAuth>} />
              <Route path="/login-info" element={<RequireAppAuth page="operations"><LoginInfoPage /></RequireAppAuth>} />

              {/* Finance section */}
              <Route path="/transactions" element={<RequireAppAuth page="finance"><TransactionsPage /></RequireAppAuth>} />
              <Route path="/reports" element={<RequireAppAuth page="finance"><ReportsPage /></RequireAppAuth>} />
              <Route path="/flexible-reports" element={<RequireAppAuth page="finance"><FlexibleReportsPage /></RequireAppAuth>} />
              <Route path="/deposits" element={<RequireAppAuth page="finance"><DepositsPage /></RequireAppAuth>} />
              <Route path="/financial-health" element={<RequireAppAuth page="financial-health"><FinancialHealthPage /></RequireAppAuth>} />

              {/* Admin */}
              <Route path="/admin/users" element={<RequireAppAuth admin><AdminUsersPage /></RequireAppAuth>} />
              <Route path="/admin/activity" element={<RequireAppAuth admin><ActivityLogPage /></RequireAppAuth>} />
              <Route path="/account" element={<RequireAppAuth><AccountSettingsPage /></RequireAppAuth>} />

              {/* Features - accessible via dashboard button, not in nav */}
              <Route path="/features" element={<FeaturesPage />} />
            </Routes>
          </ErrorBoundary>
        </AppShell>
      </RequireAppAuth>
    </PasswordGate>
  );
}

function ShareDomainNotFound() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 text-center">
      <div className="max-w-sm">
        <p className="font-mono text-6xl font-bold text-muted-foreground/40">404</p>
        <p className="mt-2 text-sm text-muted-foreground">This link is not active or the URL was mistyped.</p>
      </div>
    </div>
  );
}

function App() {
  // Domain routing modes:
  //   - share domain (`flexlivingsf.com` + `www.flexlivingsf.com`) → public
  //     marketing/listings/collections only. Login/admin surfaces MUST NOT
  //     appear here.
  //   - staff domain  (`staff.flexlivingsf.com`) → staff PWA only.
  //   - Emergent preview / admin production host → full admin app.
  //   - Any other unknown host → default to PUBLIC (safer than leaking the
  //     admin login page onto a misconfigured DNS entry).
  //
  // `?as=share` and `?as=staff` on any host preview each mode in development.
  // The `as` query is sticky for the rest of the SESSION so internal
  // navigation doesn't bounce out of preview mode — but stale `preview_as`
  // is IGNORED on real public production hosts (see filter below).
  const shareHost = (process.env.REACT_APP_SHARE_DOMAIN || '').trim().toLowerCase();
  const staffHost = (process.env.REACT_APP_STAFF_DOMAIN || '').trim().toLowerCase();
  // Public marketing hosts — exact matches only. `www.` variant is always
  // treated the same as the apex.
  const publicHosts = new Set(
    shareHost
      ? [shareHost, shareHost.startsWith('www.') ? shareHost.slice(4) : `www.${shareHost}`]
      : []
  );
  // Staff hosts — exact match only.
  const staffHosts = new Set(staffHost ? [staffHost] : []);
  // Admin production host + Emergent preview hosts. Keep this list tight so a
  // random unknown host never lands on the admin login.
  const adminHostSuffixes = ['.preview.emergentagent.com', '.emergent.host'];
  const adminExactHosts = new Set([
    (process.env.REACT_APP_ADMIN_HOST || '').trim().toLowerCase(),
    'localhost',
    '127.0.0.1',
  ].filter(Boolean));

  const currentHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

  // ── Immediate www → apex redirect ────────────────────────────────
  // Runs BEFORE React mounts anything below. Uses `location.replace` so the
  // login page never flashes. A proper 301 at the ingress is the ultimate
  // fix, but this covers the case until that's configured.
  if (
    typeof window !== 'undefined' &&
    shareHost &&
    !shareHost.startsWith('www.') &&
    currentHost === `www.${shareHost}`
  ) {
    const target = `${window.location.protocol}//${shareHost}${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(target);
    return null;
  }

  // Detect whether the current host is a known PUBLIC host BEFORE looking at
  // sessionStorage. On real public hosts, stale `preview_as` (from someone
  // previewing staff/admin earlier) must not hijack routing.
  const hostIsPublic = publicHosts.has(currentHost);
  const hostIsStaff = staffHosts.has(currentHost);
  const hostIsAdmin =
    adminExactHosts.has(currentHost) ||
    adminHostSuffixes.some((sfx) => currentHost.endsWith(sfx));

  // Handle ?as=... query param — write to sessionStorage ONLY when we're on
  // a non-production host (previews / localhost). On the real public/staff
  // production hosts the query param is ignored.
  if (typeof window !== 'undefined') {
    const param = new URLSearchParams(window.location.search).get('as');
    if ((param === 'share' || param === 'staff') && !hostIsPublic && !hostIsStaff) {
      sessionStorage.setItem('preview_as', param);
    }
    // Clear any stale preview_as on the real public / staff production hosts
    // so a browser that once had `?as=staff` on the preview URL cannot show
    // the staff PWA when the user visits flexlivingsf.com later.
    if (hostIsPublic || hostIsStaff) {
      try { sessionStorage.removeItem('preview_as'); } catch (_) { /* ignore */ }
    }
  }
  const stickyAs = (hostIsPublic || hostIsStaff)
    ? null
    : (typeof window !== 'undefined' ? sessionStorage.getItem('preview_as') : null);

  // Anyone landing on /staff/* gets the staff PWA (bookmark support) — but
  // only if the current host is either the staff host, an admin host (dev),
  // or an unknown host. Never on the public marketing hosts.
  const onStaffPath = (currentPath.startsWith('/staff/') || currentPath === '/staff');

  const isShareDomain =
    hostIsPublic ||
    (stickyAs === 'share' && !hostIsStaff);
  const isStaffDomain =
    (!isShareDomain) &&
    (hostIsStaff || (onStaffPath && !hostIsPublic) || stickyAs === 'staff');
  // Admin app renders only on hosts we explicitly recognise as admin.
  // Everything else falls through to the PUBLIC branch (never the admin
  // login).
  const isAdminDomain = !isShareDomain && !isStaffDomain && hostIsAdmin;

  if (isStaffDomain) {
    // Staff-only PWA. /staff/* is the only namespace; everything else 404s.
    return (
      <StaffAuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/staff/login" element={<StaffLoginPage />} />
            <Route path="/staff/register" element={<StaffRegisterPage />} />
            <Route path="/staff/reset-password" element={<StaffResetPasswordPage />} />
            <Route path="/staff/home" element={<RequireStaffAuth><StaffHomePage /></RequireStaffAuth>} />
            <Route path="/staff/projects" element={<RequireStaffAuth><StaffProjectsPage /></RequireStaffAuth>} />
            <Route path="/staff/projects/:projectId" element={<RequireStaffAuth><FurnitureProjectDetailPage /></RequireStaffAuth>} />
            <Route path="/staff/projects/:projectId/project-view" element={<RequireStaffAuth><ProjectViewPage /></RequireStaffAuth>} />
            <Route path="/staff/projects/:projectId/rooms/:roomId/mood-board" element={<RequireStaffAuth><RoomMoodBoardPage /></RequireStaffAuth>} />
            <Route path="/staff/projects/:projectId/rooms/:roomId/floor-plans" element={<RequireStaffAuth><RoomMoodBoardPage kind="floorplans" /></RequireStaffAuth>} />
            <Route path="/staff/hours" element={<RequireStaffAuth><StaffPayPeriodsPage /></RequireStaffAuth>} />
            <Route path="/staff/pay-periods" element={<RequireStaffAuth><StaffPayPeriodsPage /></RequireStaffAuth>} />
            <Route path="/staff/tasks" element={<RequireStaffAuth><StaffTasksPage /></RequireStaffAuth>} />
            <Route path="/staff/profile" element={<RequireStaffAuth><StaffProfilePage /></RequireStaffAuth>} />
            <Route path="/staff" element={<Navigate to="/staff/home" replace />} />
            <Route path="/" element={<Navigate to="/staff/home" replace />} />
            <Route path="*" element={<Navigate to="/staff/home" replace />} />
          </Routes>
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </StaffAuthProvider>
    );
  }

  if (isShareDomain) {
    // Public customer-facing site. No password, no admin UI, no TopNavBar.
    //   /              → Flex Living marketing landing page
    //   /booknow       → public listings search (was previously at "/")
    //   /listings/:id  → public listing detail (same UX minus admin)
    //   /:slug         → shareable collection
    //   /:slug/l/:id   → single listing inside a collection
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<FlexLivingLanding />} />
          <Route path="/booknow" element={<ListingsPage publicMode />} />
          <Route path="/listings" element={<Navigate to="/booknow" replace />} />
          <Route path="/listings/:id" element={<ListingDetailPage publicMode />} />
          <Route path="/p/:token" element={<PublicBreakdownPage />} />
          <Route path="/:slug" element={<CollectionIndexPage />} />
          <Route path="/:slug/l/:listingId" element={<CollectionListingPage />} />
          <Route path="*" element={<ShareDomainNotFound />} />
        </Routes>
        <Toaster position="bottom-right" />
      </BrowserRouter>
    );
  }

  // Unknown host → render the PUBLIC marketing site rather than falling
  // through to the admin login. This makes it impossible for a
  // mis-configured DNS entry or an unexpected preview URL to accidentally
  // expose the admin login page to search engines.
  if (!isAdminDomain) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<FlexLivingLanding />} />
          <Route path="/booknow" element={<ListingsPage publicMode />} />
          <Route path="/listings" element={<Navigate to="/booknow" replace />} />
          <Route path="/listings/:id" element={<ListingDetailPage publicMode />} />
          <Route path="/p/:token" element={<PublicBreakdownPage />} />
          <Route path="/:slug" element={<CollectionIndexPage />} />
          <Route path="/:slug/l/:listingId" element={<CollectionListingPage />} />
          <Route path="*" element={<ShareDomainNotFound />} />
        </Routes>
        <Toaster position="bottom-right" />
      </BrowserRouter>
    );
  }

  return (
    <StaffAuthProvider>
      <AppAuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Staff auth pages — accessible to anyone with a sign-up code */}
            <Route path="/staff/login" element={<StaffLoginPage />} />
            <Route path="/staff/register" element={<StaffRegisterPage />} />
            <Route path="/staff/reset-password" element={<StaffResetPasswordPage />} />
            {/* Dashboard auth pages. Note: the main login lives at
                `/portal-signin` (renamed from `/login` on 2026-02-XX to reduce
                Google indexing surface). Old `/login` bookmarks are redirected
                below for backward compatibility. */}
            <Route path="/portal-signin" element={<RedirectIfAuthed><AppLoginPage /></RedirectIfAuthed>} />
            <Route path="/login" element={<Navigate to="/portal-signin" replace />} />
            <Route path="/register" element={<RedirectIfAuthed><AppRegisterPage /></RedirectIfAuthed>} />
            <Route path="/forgot-password" element={<RedirectIfAuthed><ForgotPasswordPage /></RedirectIfAuthed>} />
            <Route path="/reset-password" element={<RedirectIfAuthed><ResetPasswordPage /></RedirectIfAuthed>} />
            {/* Admin listings — password-gated via site-wide gate, but we don't wrap
                these in AppShell so they keep their own self-contained header. */}
            <Route path="/listings" element={<PasswordGate><ListingsPage /></PasswordGate>} />
            <Route path="/listings/:id" element={<PasswordGate><ListingDetailPage /></PasswordGate>} />
            <Route path="/c/:slug" element={<CollectionIndexPage />} />
            <Route path="/c/:slug/l/:listingId" element={<CollectionListingPage />} />
            {/* Public landing preview — no dashboard chrome */}
            <Route path="/landing" element={<FlexLivingLanding />} />
            <Route path="/furniture-projects" element={<FurnitureProjectsPage />} />
            <Route path="/furniture-projects/staffing" element={<PasswordGate><FurnitureStaffingShell /></PasswordGate>} />
            <Route path="/furniture-projects/staffing/hours" element={<PasswordGate><AdminStaffHoursPage /></PasswordGate>} />
            <Route path="/furniture-projects/staffing/tasks" element={<PasswordGate><AdminStaffTasksPage /></PasswordGate>} />
            <Route path="/furniture-projects/room-defaults" element={
              <div className="min-h-screen bg-[#fbfaf7]">
                <FurnitureNavBar />
                <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                  <FurnitureRoomDefaultsPage />
                </div>
              </div>
            } />
            <Route path="/furniture-projects/receipt-processing" element={<RequireAppAuth><ReceiptProcessingPage /></RequireAppAuth>} />
            <Route path="/furniture-projects/:projectId" element={<FurnitureProjectDetailPage />} />
            <Route path="/furniture-projects/:projectId/project-view" element={<ProjectViewPage />} />
            <Route path="/furniture-projects/:projectId/rooms/:roomId/mood-board" element={<RoomMoodBoardPage />} />
            <Route path="/furniture-projects/:projectId/rooms/:roomId/floor-plans" element={<RoomMoodBoardPage kind="floorplans" />} />
            <Route path="/furniture-projects/share/:shareToken" element={<FurnitureProjectDetailPage />} />
            <Route path="/furniture-projects/share/:shareToken/project-view" element={<ProjectViewPage />} />
            <Route path="/furniture-projects/share/:shareToken/rooms/:roomId/mood-board" element={<RoomMoodBoardPage />} />
            <Route path="/furniture-projects/share/:shareToken/rooms/:roomId/floor-plans" element={<RoomMoodBoardPage kind="floorplans" />} />
            <Route path="/furniture-projects/view/:viewToken" element={<FurnitureProjectDetailPage />} />
            <Route path="/furniture-projects/view/:viewToken/project-view" element={<ProjectViewPage />} />
            <Route path="/furniture-projects/view/:viewToken/rooms/:roomId/mood-board" element={<RoomMoodBoardPage />} />
            <Route path="/furniture-projects/view/:viewToken/rooms/:roomId/floor-plans" element={<RoomMoodBoardPage kind="floorplans" />} />
            {/* Public cost-breakdown share links — no auth, no chrome. */}
            <Route path="/p/:token" element={<PublicBreakdownPage />} />
            <Route path="/breakdown/:token" element={<PublicBreakdownPage />} />
            <Route path="/*" element={<ProtectedApp />} />
          </Routes>
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </AppAuthProvider>
    </StaffAuthProvider>
  );
}

export default App;
